/**
 * P36 (Feb 28, 2026) — Section card grid for the unified Billing Hub.
 *
 * Each card represents one functional area inside Billing. Click a
 * card → navigates to that area's primary page. The grid replaces the
 * 6-item sidebar group with a friendlier visual hub.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, WarningCircle, FileArrowDown, Brain, Buildings, ArrowRight,
} from '@phosphor-icons/react';

const SECTIONS = [
  {
    key: 'claims',
    title: 'Claims',
    description: 'Review, edit, submit and manage every claim across all payers. Tabs for Recent, Drafts, Issues.',
    icon: FileText,
    iconBg: 'bg-indigo-100 text-indigo-700',
    cardClass: 'hover:border-indigo-300',
    path: '/claims',
    testid: 'hub-card-claims',
  },
  {
    key: 'denials',
    title: 'Denials & Rejections',
    description: '277CA + 999 + ERA denial workflows. Triage rejected claims and re-submit with corrections.',
    icon: WarningCircle,
    iconBg: 'bg-rose-100 text-rose-700',
    cardClass: 'hover:border-rose-300',
    path: '/claims/denials',
    testid: 'hub-card-denials',
  },
  {
    key: 'odm',
    title: 'ODM Claims',
    description: 'Configure the Ohio Medicaid SFTP submitter — connection settings, inbound/outbound folders, notifications.',
    icon: FileArrowDown,
    iconBg: 'bg-sky-100 text-sky-700',
    cardClass: 'hover:border-sky-300',
    path: '/odm-config',
    testid: 'hub-card-odm',
  },
  {
    key: 'bh',
    title: 'BH Billing',
    description: 'Behavioral-health billing workflow — independent claim types, code lookups, and payer routing.',
    icon: Brain,
    iconBg: 'bg-purple-100 text-purple-700',
    cardClass: 'hover:border-purple-300',
    path: '/bh-billing',
    testid: 'hub-card-bh',
  },
  {
    key: 'dodd',
    title: 'DODD Billing',
    description: 'Ohio Department of Developmental Disabilities — CODB tier × Servicing rate matrix pricing.',
    icon: Buildings,
    iconBg: 'bg-amber-100 text-amber-700',
    cardClass: 'hover:border-amber-300',
    path: '/dodd',
    testid: 'hub-card-dodd',
  },
];

const HubSectionCards = () => {
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="hub-section-cards">
      {SECTIONS.map((section) => {
        const Icon = section.icon;
        return (
          <button
            key={section.key}
            onClick={() => navigate(section.path)}
            className={`group text-left p-6 bg-white rounded-2xl border border-slate-200 transition-all hover:shadow-lg ${section.cardClass}`}
            data-testid={section.testid}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${section.iconBg}`}>
                <Icon size={26} weight="fill" />
              </div>
              <ArrowRight size={18} className="text-slate-300 group-hover:text-slate-700 transition-colors" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">{section.title}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">{section.description}</p>
          </button>
        );
      })}
    </div>
  );
};

export default HubSectionCards;
